import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useDialog } from '@gokrew/ui';
import { KollabHRFullLogo } from '@gokrew/ui/assets';
import {
  LayoutDashboard,
  Columns3,
  Building2,
  UsersRound,
  ListTodo,
  Mail,
  Command as CommandIcon,
  Settings,
  Target,
  Presentation,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { useAuth } from '@/context/AuthContext';
import { hasCrmAccess } from './crm-access';
import { CrmButton } from './CrmShared';
import { CrmCommandPalette, isMac } from './CrmCommandPalette';
import { CrmSettings } from './CrmSettings';
import { personInitials } from './crm-format';
import { useCrmLeadsHidden } from './crm-leads-hidden';
import styles from './Crm.module.css';

const navigation = [
  { path: '/crm', key: 'dashboard', Icon: LayoutDashboard },
  { path: '/crm/leads', key: 'leads', Icon: Target },
  { path: '/crm/deals', key: 'deals', Icon: Columns3 },
  { path: '/crm/companies', key: 'companies', Icon: Building2 },
  { path: '/crm/contacts', key: 'contacts', Icon: UsersRound },
  { path: '/crm/activities', key: 'activities', Icon: ListTodo },
  { path: '/crm/inbox', key: 'inbox', Icon: Mail },
  { path: '/crm/demos', key: 'demos', Icon: Presentation },
];
export const CrmShell = () => {
  const { user } = useAuth();
  const { t } = useTranslation(TranslationNS_Admin.CompanyInfo);
  const allowed = hasCrmAccess(user?.email);
  const navigate = useNavigate();
  const leadsHidden = useCrmLeadsHidden();
  const [palette, setPalette] = useState(false);
  const { openDialog } = useDialog();
  const openSettings = useCallback(
    () =>
      openDialog({
        title: t('crm.settings.title'),
        containerClass: styles.editorDialog,
        content: <CrmSettings />,
      }),
    [openDialog, t]
  );
  useEffect(() => {
    if (!allowed) return;
    // Cmd+K (Ctrl+K) opens the quick actions, Cmd+O goes to Opportunities.
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey)
        return;
      const key = event.key.toLowerCase();
      if (key === 'k') {
        event.preventDefault();
        setPalette((open) => !open);
      } else if (key === 'o') {
        event.preventDefault();
        setPalette(false);
        navigate('/crm/deals');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allowed, navigate]);
  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.mobileAccount}>
          <img src={KollabHRFullLogo} alt="KollabHR" />
          <div>
            <CrmButton
              variant="ghost"
              IconPrefix={Settings}
              aria-label={t('crm.settings.open')}
              onClick={openSettings}
            />
          </div>
        </div>
        <NavLink
          to="/crm"
          className={styles.brand}
          aria-label={t('crm.workspace.dashboard')}
        >
          <img src={KollabHRFullLogo} alt="KollabHR" />
          <span>{t('crm.workspace.sales_workspace')}</span>
        </NavLink>
        {allowed && (
          <button
            type="button"
            className={styles.paletteTrigger}
            onClick={() => setPalette(true)}
          >
            <CommandIcon aria-hidden="true" />
            <span>{t('crm.palette.title')}</span>
            <kbd>{isMac() ? '⌘K' : 'Ctrl K'}</kbd>
          </button>
        )}
        <p className={styles.navLabel}>{t('crm.workspace.workspace')}</p>
        <nav
          className={styles.navigation}
          aria-label={t('crm.workspace.navigation')}
        >
          {navigation
            .filter(({ key }) => key !== 'leads' || !leadsHidden)
            .map(({ path, key, Icon }) => (
              <NavLink
                key={path}
                to={path}
                end={path === '/crm'}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.navActive : ''}`
                }
              >
                <Icon aria-hidden="true" />
                <span>{t(`crm.workspace.${key}`)}</span>
              </NavLink>
            ))}
        </nav>
        <div className={styles.sidebarBottom}>
          <div className={styles.accountRow}>
            <button
              type="button"
              className={`${styles.account} ${styles.accountButton}`}
              aria-label={t('crm.settings.open')}
              onClick={openSettings}
            >
              <span className={styles.avatar}>
                {personInitials({
                  firstName: user?.name?.split(' ')[0],
                  lastName: user?.name?.split(' ').slice(1).join(' '),
                  email: user?.email,
                }) || 'K'}
              </span>
              <strong>{user?.name}</strong>
            </button>
            <CrmButton
              variant="ghost"
              IconPrefix={Settings}
              aria-label={t('crm.settings.open')}
              onClick={openSettings}
            />
          </div>
        </div>
      </aside>
      <main className={styles.workspaceMain}>
        {allowed ? (
          <Outlet />
        ) : (
          <div className={styles.empty}>
            <h1>{t('crm.restricted')}</h1>
            <p>{t('crm.restricted_description')}</p>
          </div>
        )}
      </main>
      {allowed && (
        <CrmCommandPalette
          open={palette}
          onOpenChange={setPalette}
          onSettings={openSettings}
        />
      )}
    </div>
  );
};
